/**
 * Shared pure-math scene-geometry builder for MTEXT (DEFUSE — clone
 * elimination).
 *
 * Takes the already-extracted MTEXT primitive inputs (raw insertion `Vec3`,
 * height, rotation, width factor, line-spacing factor, attachment point,
 * resolved style name, contents string + the placeholder colour for runs that
 * carry no explicit `\C` override) and returns the `{ geometry, bounds }` pair
 * that BOTH the legacy `../mtextToRenderable.ts` and the typed
 * `../typed/mtextFromEntity.ts` adapter feed into `buildRenderableShell`.
 *
 * Why this module exists: the legacy adapter (reads the `EntityPreview`
 * `properties` string bag) and its typed sibling (reads the canonical
 * `MtextEntity` fields) are byte-identical AFTER value extraction — the SAME
 * `parseMTextContent` → `dropLineBreakFlag` → `mtext` SceneGeometry assembly +
 * the SAME conservative line-count / longest-line bbox. That is one semantic
 * truth (the MText render-geometry math), duplicated only because the two
 * paths differ in their value source. The math now lives here ONCE.
 *
 * `applyOffset` is applied INSIDE the builder to the absolute insertion point —
 * exactly as the legacy adapter did. The placeholder-colour run backfill needs
 * the resolved `RenderableEntity.colorHex` (only available after
 * `buildRenderableShell`), so it is NOT part of geometry assembly — but the
 * shared `backfillMTextRunColors` helper + the `MTEXT_PLACEHOLDER_COLOR`
 * sentinel (both below) live here so the two adapters call ONE truth after the
 * shell is built rather than each copying the loop + the sentinel literal.
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond
 * reading the locked-in coordinate origin via `applyOffset`.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { mtextBlockSpan, type DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { applyOffset } from "../../coordinateOrigin";
import {
  parseMTextContent,
  type MTextParsedRun,
} from "../../text/mtextParser";
import type {
  MTextRun,
  RenderableEntity,
  SceneGeometry,
  Vec3,
} from "../../types";
import {
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import type { SceneGeometryResult } from "../sceneGeometryBuilders";

/**
 * Strip the PR-E3-only `lineBreak` marker so the runs stored on the public
 * `mtext` SceneGeometry match the public `MTextRun` shape exactly. (Identical
 * to the legacy adapter's local helper; lives here so both paths share it.)
 */
function dropLineBreakFlag(runs: MTextParsedRun[]): MTextRun[] {
  return runs.map<MTextRun>((r) => {
    const { lineBreak: _lineBreak, ...publicShape } = r;
    void _lineBreak;
    return publicShape;
  });
}

/** Inputs the builder needs — all already extracted from the value source. */
export interface MTextGeometryInput {
  /** Raw (pre-offset) insertion point. */
  insertionRaw: Vec3;
  /** MTEXT base height (group code 40). */
  height: number;
  /** Rotation in degrees CCW. */
  rotation: number;
  /** Width factor (group code 41). */
  widthFactor: number;
  /** Line spacing factor (group code 44). */
  lineSpacingFactor: number;
  /** Attachment point (group code 71, 1..9). */
  attachmentPoint: number;
  /** Resolved STYLE name. */
  styleName: string;
  /** Raw MTEXT contents string (inline DXF format codes). */
  contents: string;
  /**
   * Codex #342 F1+F3 — MTEXT OCS extrusion handedness. `true` ⇒ the extrusion Z
   * is negative (a true mirror, not a rotation), so the live mesh path flips the
   * glyph frame on the local Y axis (upside-down / handedness equivalent — MTEXT
   * has no group-71, so the bit-4 derivation in `geometryTransforms.ts` applies;
   * the mesh sets `scale.set(1,-1,1)`). Defaults to `false` (non-mirrored MTEXT →
   * byte-identical golden-parity output).
   */
  mirrored?: boolean;
  /**
   * Placeholder colour assigned to runs that carry NO explicit `\C` override.
   * Both adapters backfill the resolved entity colour over this placeholder on
   * the returned `geometry.runs` AFTER `buildRenderableShell`.
   */
  placeholderColor: string;
}

/**
 * Build the `mtext` geometry + conservative AABB from the already-extracted
 * MTEXT inputs. `applyOffset` is applied to the insertion point (identical to
 * the legacy adapter). The bbox mirrors the legacy heuristic exactly: insertion
 * ± (longest line × height × widthFactor × 0.6, the painted block span
 * `mtextBlockSpan`), counting `\P` / `\n` line-break runs as paragraph
 * separators.
 */
export function buildMTextSceneGeometry(
  input: MTextGeometryInput,
): SceneGeometryResult {
  const {
    insertionRaw,
    height,
    rotation,
    widthFactor,
    lineSpacingFactor,
    attachmentPoint,
    styleName,
    contents,
    mirrored,
    placeholderColor,
  } = input;

  const position = applyOffset(insertionRaw);

  const parsedRuns = parseMTextContent(contents, {
    fontStyleName: styleName,
    heightWorld: height,
    widthFactor,
    colorHex: placeholderColor,
  });
  const runs = dropLineBreakFlag(parsedRuns);

  const geometry: SceneGeometry = {
    kind: "mtext",
    position,
    rotation,
    height,
    widthFactor,
    styleName,
    runs,
    lineSpacingFactor,
    attachmentPoint,
    // Codex #342 F1+F3 — extrusion handedness flip (false ⇒ no flip; golden parity).
    mirrored: mirrored ?? false,
  };

  // Conservative bbox: insertion ± (longest line × height × widthFactor × 0.6,
  // the painted block span). Line-break runs are counted as paragraph
  // separators so the height matches the visible layout.
  const lineCount = parsedRuns.filter((r) => r.lineBreak).length + 1;
  const longestLineChars = (() => {
    let cur = 0;
    let longest = 0;
    for (const r of parsedRuns) {
      if (r.lineBreak) {
        if (cur > longest) longest = cur;
        cur = 0;
        continue;
      }
      cur += r.text.length;
    }
    if (cur > longest) longest = cur;
    return longest;
  })();
  const halfX = Math.max(
    1,
    height * widthFactor * Math.max(longestLineChars, 1) * 0.6,
  );
  // `halfY` is the FULL painted block span applied in BOTH directions, not half
  // of it — the same "full extent each way" convention `halfX` above and the
  // TEXT sibling (`textGeometry.ts`) use, which is what lets this AABB ignore
  // `attachmentPoint` entirely and still cover all nine anchors. The SYMMETRY is
  // therefore deliberate and stays; only the MAGNITUDE was wrong.
  //
  // It read `height × lineCount × lineSpacingFactor`, which omits the 1.6 the
  // renderer's own line advance carries (`sceneTextMeshFactories.ts`:
  // `lineHeight = g.height × g.lineSpacingFactor × 1.6`) and counts `lineCount`
  // line-heights where `lineCount − 1` baseline drops plus one cap height are
  // painted — converging to a 37.5% under-report. A 10-paragraph note at height
  // 2.5 claimed 25 units against a painted 38.5, so its lowest ~3.4 lines fell
  // outside the AABB that `lodPolicy.ts`'s sub-pixel gate, `visibleSet.ts`'s
  // culling, `spatialIndex.ts` and `raycast/pick.ts` all trust. Shared with the
  // model-layer box via `mtextBlockSpan` so a third copy cannot drift again.
  const halfY = Math.max(1, mtextBlockSpan(height, lineCount, lineSpacingFactor));
  const bounds = {
    minX: position.x - halfX,
    minY: position.y - halfY,
    maxX: position.x + halfX,
    maxY: position.y + halfY,
  };

  return { geometry, bounds };
}

/**
 * Placeholder colour assigned to MTEXT runs that carry NO explicit `\C`
 * override at parse time. Both adapters pass this as `placeholderColor` to
 * `buildMTextSceneGeometry`, then call `backfillMTextRunColors` to replace it
 * with the resolved entity colour after the shell is built. One sentinel truth
 * so the colour the builder writes and the colour the backfill matches cannot
 * drift apart.
 */
export const MTEXT_PLACEHOLDER_COLOR = "#ffffff";

/**
 * Backfill the resolved entity colour onto every run still carrying the
 * placeholder (i.e. no explicit `\C` override); runs that set their own colour
 * keep it. Mutates `geometry.runs` in place — both the legacy and typed MTEXT
 * adapters call this AFTER `buildRenderableShell` (the resolved colour does not
 * exist until then), so the post-shell backfill is ONE shared truth, not a
 * per-adapter copy. `buildMTextSceneGeometry` always returns the `mtext` kind,
 * so the narrowing guard is a type assertion, not a behavioural branch.
 */
export function backfillMTextRunColors(
  geometry: SceneGeometry,
  resolvedColorHex: string,
): void {
  if (geometry.kind !== "mtext") return;
  for (const run of geometry.runs) {
    if (run.colorHex === MTEXT_PLACEHOLDER_COLOR) {
      run.colorHex = resolvedColorHex;
    }
  }
}

/**
 * Assemble the final MTEXT `RenderableEntity` from the already-extracted
 * primitive inputs. This is the one shared truth for the MTEXT assembly tail —
 * build the `mtext` geometry (with the placeholder sentinel), build the
 * renderable shell, then backfill the resolved colour over the placeholder.
 * Both the legacy (`../mtextToRenderable.ts`, string bag) and typed
 * (`../typed/mtextFromEntity.ts`, canonical model) adapters call this; they
 * differ ONLY in how they derive `input` (the value source), so the assembly +
 * shell + backfill is not copied between them. Accepts either entity shape — the
 * `buildRenderableShell` union overload routes it via the runtime
 * `"properties" in entity` discrimination.
 */
export function assembleMTextRenderable(
  entity: EntityPreview | DrawingEntity,
  ctx: AdapterContext,
  input: Omit<MTextGeometryInput, "placeholderColor">,
): RenderableEntity {
  const { geometry, bounds } = buildMTextSceneGeometry({
    ...input,
    placeholderColor: MTEXT_PLACEHOLDER_COLOR,
  });
  const renderable = buildRenderableShell(entity, ctx, geometry, bounds);
  backfillMTextRunColors(geometry, renderable.colorHex);
  return renderable;
}
